/**
 * Página de Onboarding - Criação de Tenant
 * 
 * Wizard multi-step para criação de empresa (tenant) com layout split-screen moderno.
 * Inclui validação de inputs, sanitização de dados e rate limiting visual.
 * 
 * Layout:
 * - Sidebar esquerda (40%): Logo, stepper vertical, background decorativo
 * - Área de conteúdo (60%): Formulários de cada passo
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useSlugValidation, generateSlugFromName } from '@/hooks/useSlugValidation';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { logger } from '@/lib/logger';
import { getFriendlyOnboardingError } from '@/lib/onboardingErrors';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Building2, CheckCircle2, XCircle, Loader2, ArrowRight, Sparkles, MessageSquare, Zap, Check, Rocket, AlertTriangle, Search, LogOut } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { validateCPF, validateCNPJ } from '@/lib/input-validators';
import { countryCodes } from '@/lib/country-codes';
import { ChevronsUpDown } from 'lucide-react';

/** Aplica máscara de CPF (000.000.000-00) */
const maskCPF = (v: string) => v.replace(/\D/g, '').slice(0, 11)
  .replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
/** Aplica máscara de CNPJ (00.000.000/0000-00) */
const maskCNPJ = (v: string) => v.replace(/\D/g, '').slice(0, 14)
  .replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
  .replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2');
/** Aplica máscara de CEP (00000-000) */
const maskCEP = (v: string) => v.replace(/\D/g, '').slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2');

// Tipo para planos de assinatura (baseado no schema real da tabela)
interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  price_monthly: number;
  max_inboxes: number;
  max_agents: number;
  max_messages_month: number;
  max_channels: number;
  is_active: boolean;
  sort_order: number;
}

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface CreateTenantResult {
  success: boolean;
  error?: string;
  message?: string;
  tenant_id?: string;
  slug?: string;
  trial_ends_at?: string;
}

// ============================================================================
// FUNÇÕES UTILITÁRIAS DE VALIDAÇÃO E SANITIZAÇÃO
// ============================================================================

/**
 * Sanitiza input removendo caracteres potencialmente perigosos
 * Previne XSS e injection attacks
 */
const sanitizeInput = (input: string): string => {
  // Nao aplicar .trim() aqui: em onChange isso removeria o espaco recem-digitado
  // antes da proxima tecla, impedindo o usuario de digitar espacos entre palavras.
  // O trim final deve ser feito no momento do submit.
  return input.replace(/[<>]/g, '') // Remove < e > para prevenir HTML injection
  .replace(/javascript:/gi, '') // Remove javascript: protocol
  .replace(/on\w+=/gi, '') // Remove event handlers inline
  .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, ''); // Remove control characters
};

/**
 * Valida nome da empresa com regras de segurança
 */
const validateCompanyName = (name: string): {
  valid: boolean;
  message?: string;
} => {
  const sanitized = sanitizeInput(name);
  if (sanitized.length < 2) {
    return {
      valid: false,
      message: 'Nome deve ter pelo menos 2 caracteres'
    };
  }
  if (sanitized.length > 25) {
    return {
      valid: false,
      message: 'Nome deve ter no máximo 25 caracteres'
    };
  }

  // Permite letras (incluindo acentuadas), números, espaços, hífen e ponto
  const validPattern = /^[\p{L}\p{N}\s\-\.]+$/u;
  if (!validPattern.test(sanitized)) {
    return {
      valid: false,
      message: 'Nome contém caracteres inválidos'
    };
  }
  return {
    valid: true
  };
};

// ============================================================================
// DADOS DOS PASSOS DO WIZARD
// ============================================================================

const STEPS = [{
  id: 1,
  title: 'Dados da Empresa',
  description: 'Informações básicas da sua empresa',
  icon: Building2
}, {
  id: 2,
  title: 'Tudo Pronto!',
  description: 'Acesse seu painel',
  icon: Sparkles
}];
// PLAN_FEATURES agora é dinâmico baseado nos planos

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function Onboarding() {
  const navigate = useNavigate();
  const {
    user,
    signOut
  } = useAuth();
  const {
    branding
  } = useSystemSettings();
  const {
    status: slugStatus,
    message: slugMessage,
    normalizedSlug,
    checkSlug
  } = useSlugValidation();

  // Estados do wizard
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estados do formulário
  const [companyName, setCompanyName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [companyNameValidation, setCompanyNameValidation] = useState<{
    valid: boolean;
    message?: string;
  }>({
    valid: true
  });

  // Estados de rate limiting visual
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);

  // Nota: seleção de plano removida do onboarding — o plano padrão é
  // resolvido no backend via subscription_plans.is_default_for_new_tenant.


  // ==========================================================================
  // [v3.1.0] Novos campos de perfil da empresa (país, PF/PJ, fiscais, dor)
  // ==========================================================================
  // Seletor de DDI: valor guardado é o próprio código E.164 (ex.: '+55').
  // Brasil ('+55') ativa o fluxo fiscal completo (PF/PJ, CPF/CNPJ, endereço).
  const [dialCode, setDialCode] = useState<string>('+55');
  const [dialPopoverOpen, setDialPopoverOpen] = useState(false);
  const [personType, setPersonType] = useState<'pf' | 'pj'>('pf');
  const [taxId, setTaxId] = useState('');            // CPF ou CNPJ (mascarado)
  const [taxIdError, setTaxIdError] = useState<string | null>(null);
  const [cep, setCep] = useState('');
  const [cepError, setCepError] = useState<string | null>(null);
  const [uf, setUf] = useState('');
  const [cidade, setCidade] = useState('');
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [industry, setIndustry] = useState('');
  const [mainPain, setMainPain] = useState('');

  const isBR = dialCode === '+55';
  // País selecionado (primeiro match por DDI). Guardamos a referência para
  // exibir bandeira + nome no trigger e derivar ISO2 no envio ao backend.
  const selectedCountry = countryCodes.find(c => c.code === dialCode) ?? countryCodes[0];

  // ============================================================================
  // EFEITOS
  // ============================================================================

  // Countdown do rate limiting
  useEffect(() => {
    if (rateLimitCountdown > 0) {
      const timer = setTimeout(() => {
        setRateLimitCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (rateLimitCountdown === 0 && isRateLimited) {
      setIsRateLimited(false);
    }
  }, [rateLimitCountdown, isRateLimited]);

  // Nota: efeitos de fetchPlans e sincronização do carrossel foram removidos,
  // pois o passo "Confirme seu Plano" saiu do onboarding.


  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleCompanyNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const sanitizedValue = sanitizeInput(rawValue);
    setCompanyName(sanitizedValue);

    // Valida o nome
    const validation = validateCompanyName(sanitizedValue);
    setCompanyNameValidation(validation);

    // Auto-gera slug se não foi editado manualmente
    if (!slugManuallyEdited && sanitizedValue) {
      const generatedSlug = generateSlugFromName(sanitizedValue);
      setSlug(generatedSlug);
      checkSlug(generatedSlug);
    }
  }, [slugManuallyEdited, checkSlug]);
  const handleSlugChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.toLowerCase();
    const sanitizedValue = sanitizeInput(rawValue).replace(/[^a-z0-9-]/g, '');
    setSlug(sanitizedValue);
    setSlugManuallyEdited(true);
    if (sanitizedValue) {
      checkSlug(sanitizedValue);
    }
  }, [checkSlug]);
  // --------------------------------------------------------------------------
  // [v3.1.0] Handlers dos novos campos
  // --------------------------------------------------------------------------
  /** Consulta o ViaCEP quando o CEP tiver 8 dígitos e preenche UF/Cidade. */
  const handleCepBlur = useCallback(async () => {
    const clean = cep.replace(/\D/g, '');
    if (clean.length !== 8) {
      setCepError(clean.length > 0 ? 'CEP deve ter 8 dígitos' : null);
      return;
    }
    setCepError(null);
    setIsLoadingCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const data = await resp.json();
      if (data.erro) {
        setCepError('CEP não encontrado');
        return;
      }
      setUf(String(data.uf || '').toUpperCase());
      setCidade(String(data.localidade || ''));
    } catch {
      setCepError('Erro ao consultar CEP');
    } finally {
      setIsLoadingCep(false);
    }
  }, [cep]);

  /** Aplica máscara conforme PF/PJ e limpa erro anterior. */
  const handleTaxIdChange = useCallback((raw: string) => {
    setTaxId(personType === 'pf' ? maskCPF(raw) : maskCNPJ(raw));
    setTaxIdError(null);
  }, [personType]);

  /** Validação client-side dos campos do passo 1. */
  const step1ExtraValid = (() => {
    if (!industry.trim() || industry.trim().length > 50) return false;
    if (!mainPain.trim() || mainPain.trim().length > 200) return false;
    if (!isBR) return true;
    if (personType !== 'pf' && personType !== 'pj') return false;
    const digits = taxId.replace(/\D/g, '');
    if (personType === 'pf' && digits.length !== 11) return false;
    if (personType === 'pj' && digits.length !== 14) return false;
    if (cep.replace(/\D/g, '').length !== 8) return false;
    if (!uf.trim() || !cidade.trim()) return false;
    return true;
  })();

  const canProceedToStep2 =
    companyName.trim().length >= 2 &&
    companyNameValidation.valid &&
    slugStatus === 'available' &&
    !isRateLimited &&
    step1ExtraValid;

  const handleCreateTenant = async () => {
    if (!canProceedToStep2 || isRateLimited) return;

    // Validação final antes de submeter
    const nameValidation = validateCompanyName(companyName);
    if (!nameValidation.valid) {
      toast.error(nameValidation.message || 'Nome da empresa inválido');
      return;
    }
    if (slugStatus !== 'available') {
      toast.error('O identificador URL não está disponível');
      return;
    }

    // Validação de dígitos CPF/CNPJ para BR
    if (isBR) {
      const digits = taxId.replace(/\D/g, '');
      const check = personType === 'pf' ? validateCPF(digits) : validateCNPJ(digits);
      if (!check.isValid) {
        setTaxIdError(check.error || 'Documento inválido');
        toast.error(check.error || 'Documento inválido');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      logger.info('[Onboarding] Criando tenant para:', sanitizeInput(companyName).trim());
      const { data, error } = await supabase.rpc('create_tenant_self_service', {
        _user_id: user?.id || '',
        _tenant_name: sanitizeInput(companyName).trim(),
        _tenant_slug: sanitizeInput(normalizedSlug || slug).trim(),
        _plan: 'free',
        _country_code: selectedCountry?.iso2 ?? null,
        _person_type: isBR ? personType : null,
        _tax_id: isBR ? taxId.replace(/\D/g, '') : null,
        _cep: isBR ? cep.replace(/\D/g, '') : null,
        _uf: isBR ? uf.trim().toUpperCase() : null,
        _cidade: isBR ? cidade.trim() : null,
        _industry: sanitizeInput(industry).trim().slice(0, 50),
        _main_pain: sanitizeInput(mainPain).trim().slice(0, 200),
      });
      if (error) {
        logger.error('[Onboarding] Erro RPC:', error.message);

        // Detecta rate limiting do backend
        if (error.message?.includes('rate') || error.message?.includes('limite')) {
          setIsRateLimited(true);
          setRateLimitCountdown(60);
          toast.error('Muitas tentativas. Aguarde 60 segundos.');
          return;
        }
        toast.error(getFriendlyOnboardingError(error.message));
        return;
      }

      // Cast seguro para o tipo esperado
      const result = data as unknown as CreateTenantResult;
      if (!result || !result.success) {
        logger.warn('[Onboarding] Falha na criação:', result?.message);
        if (result?.error === 'rate_limited') {
          setIsRateLimited(true);
          setRateLimitCountdown(60);
          toast.error('Muitas tentativas. Aguarde antes de tentar novamente.');
          return;
        }
        if (result?.error === 'already_has_tenant') {
          toast.error('Você já possui uma empresa cadastrada.');
          setTimeout(() => navigate('/tenant', {
            replace: true
          }), 2000);
          return;
        }
        toast.error(getFriendlyOnboardingError(result?.message));
        return;
      }

      // Sucesso!
      logger.info('[Onboarding] Tenant criado com sucesso:', result.slug);
      toast.success('Empresa criada com sucesso!');
      setCurrentStep(2);
    } catch (err) {
      logger.error('[Onboarding] Exceção:', String(err));
      toast.error(getFriendlyOnboardingError(err instanceof Error ? err.message : String(err)));
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleAccessPanel = () => {
    // Redireciona forçando reload para carregar o novo tenant
    window.location.href = '/tenant';
  };

  /**
   * Encerra a sessão do usuário e redireciona para /auth.
   * Permite sair do onboarding caso a criação do tenant esteja bloqueada.
   */
  const handleLogout = () => {
    try {
      signOut(() => {
        toast.success('Sessão encerrada.');
      });
      // Redirecionamento imediato — signOut faz o restante em background
      navigate('/auth', { replace: true });
    } catch (err) {
      logger.error('[Onboarding] Falha ao deslogar:', String(err));
      window.location.href = '/auth';
    }
  };

  // ============================================================================
  // COMPONENTES DE RENDERIZAÇÃO
  // ============================================================================

  /**
   * Renderiza o indicador de status do slug
   */
  const renderSlugStatus = () => {
    if (!slug) return null;
    switch (slugStatus) {
      case 'checking':
        return <div className="flex items-center gap-2 text-muted-foreground text-sm mt-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Verificando disponibilidade...</span>
          </div>;
      case 'available':
        return <div className="flex items-center gap-2 text-green-600 text-sm mt-2">
            <CheckCircle2 className="h-4 w-4" />
            <span>Disponível!</span>
          </div>;
      case 'unavailable':
        return <div className="flex items-center gap-2 text-destructive text-sm mt-2">
            <XCircle className="h-4 w-4" />
            <span>{slugMessage || 'Este identificador já está em uso'}</span>
          </div>;
      case 'error':
        return <div className="flex items-center gap-2 text-destructive text-sm mt-2">
            <XCircle className="h-4 w-4" />
            <span>{slugMessage || 'Erro ao verificar'}</span>
          </div>;
      default:
        return null;
    }
  };

  /**
   * Renderiza o stepper vertical na sidebar
   */
  const renderVerticalStepper = () => <div className="flex flex-col gap-0">
      {STEPS.map((step, index) => {
      const isActive = currentStep === step.id;
      const isCompleted = currentStep > step.id;
      const isLast = index === STEPS.length - 1;
      const StepIcon = step.icon;
      return <div key={step.id} className="flex gap-4">
            {/* Coluna do ícone e linha */}
            <div className="flex flex-col items-center">
              {/* Círculo do ícone */}
              <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300
                  ${isCompleted ? 'bg-green-500 text-white' : isActive ? 'bg-white text-primary shadow-lg shadow-white/20' : 'bg-white/10 text-white/50'}
                `}>
                {isCompleted ? <Check className="h-6 w-6" /> : <StepIcon className="h-6 w-6" />}
              </div>
              
              {/* Linha conectora */}
              {!isLast && <div className={`
                    w-0.5 h-16 transition-all duration-300
                    ${isCompleted ? 'bg-green-500' : 'bg-white/20'}
                  `} />}
            </div>

            {/* Conteúdo do step */}
            <div className="pt-2 pb-8">
              <h3 className={`
                  font-semibold text-base transition-colors duration-300
                  ${isActive ? 'text-white' : isCompleted ? 'text-green-400' : 'text-white/50'}
                `}>
                {step.title}
              </h3>
              <p className={`
                  text-sm mt-1 transition-colors duration-300
                  ${isActive ? 'text-white/80' : 'text-white/40'}
                `}>
                {step.description}
              </p>
            </div>
          </div>;
    })}
    </div>;

  /**
   * Renderiza o conteúdo do Step 1 - Dados da Empresa
   */
  const renderStep1Content = () => <div className="space-y-6 animate-fade-in">
      {/* Alerta de rate limiting */}
      {isRateLimited && <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Muitas tentativas. Aguarde {rateLimitCountdown} segundos antes de tentar novamente.
          </AlertDescription>
        </Alert>}

      {/* Campo: Nome da Empresa */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="companyName" className="text-base font-medium">
            Nome da Empresa
          </Label>
          {/* Contador de caracteres (padrão SaaS) — destaca em vermelho ao atingir o limite */}
          <span className={`text-xs ${companyName.length >= 25 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
            {companyName.length}/25
          </span>
        </div>
        <Input id="companyName" placeholder="Ex: Minha Empresa" value={companyName} onChange={handleCompanyNameChange} disabled={isRateLimited} maxLength={25} className="h-12 text-base rounded-xl border-border/50 focus:border-primary transition-colors" />
        {!companyNameValidation.valid && companyName && <p className="text-sm text-destructive flex items-center gap-2">
            <XCircle className="h-4 w-4" />
            {companyNameValidation.message}
          </p>}
      </div>


      {/* Campo: Identificador URL */}
      <div className="space-y-2">
        <Label htmlFor="slug" className="text-base font-medium">
          Identificador URL
        </Label>
        <div className="relative">
          <Input id="slug" placeholder="minha-empresa" value={slug} onChange={handleSlugChange} disabled={isRateLimited} maxLength={50} className="h-12 text-base rounded-xl border-border/50 focus:border-primary transition-colors font-mono" />
        </div>
        {renderSlugStatus()}
        <p className="text-sm text-muted-foreground mt-2">
          Seu painel será acessível em:{' '}
          <span className="font-mono text-foreground">
            app.uaizychat.com.br/{slug || 'sua-empresa'}
          </span>
        </p>
      </div>

      {/* ================================================================== */}
      {/* [v3.1.0] Perfil da Empresa — País, PF/PJ, endereço, ramo, dor      */}
      {/* ================================================================== */}

      {/* Campo: DDI / País — combobox pesquisável com a lista completa.
          O valor guardado é o próprio DDI (ex.: '+55'); ISO2 é derivado
          apenas no envio ao backend para preservar o contrato da RPC. */}
      <div className="space-y-2">
        <Label className="text-base font-medium">DDI / País</Label>
        <Popover open={dialPopoverOpen} onOpenChange={setDialPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={dialPopoverOpen}
              disabled={isRateLimited}
              className="w-full h-12 text-base rounded-xl border-border/50 justify-between font-normal"
            >
              <span className="flex items-center gap-2 truncate">
                <span className="text-lg leading-none">{selectedCountry.flag}</span>
                <span className="font-mono font-medium">{selectedCountry.code}</span>
                <span className="text-muted-foreground truncate">— {selectedCountry.country}</span>
              </span>
              <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 w-[--radix-popover-trigger-width] max-w-none"
            align="start"
          >
            <Command
              // Filtro custom para buscar por nome, DDI ou ISO2
              filter={(value, search) => {
                const q = search.toLowerCase().replace(/\s+/g, '');
                return value.toLowerCase().includes(q) ? 1 : 0;
              }}
            >
              <CommandInput placeholder="Buscar país ou DDI (ex.: brasil, +55)" className="h-11" />
              <CommandList className="max-h-72">
                <CommandEmpty>Nenhum país encontrado.</CommandEmpty>
                <CommandGroup>
                  {countryCodes.map((c, idx) => {
                    // Chave composta pois alguns países compartilham DDI (ex.: +1)
                    const key = `${c.iso2 ?? 'xx'}-${c.code}-${idx}`;
                    // Value contém tudo que é pesquisável
                    const value = `${c.country} ${c.code} ${c.iso2 ?? ''}`;
                    const isSelected = c.code === dialCode && c.iso2 === selectedCountry.iso2;
                    return (
                      <CommandItem
                        key={key}
                        value={value}
                        onSelect={() => {
                          setDialCode(c.code);
                          // Força o "selectedCountry" para o item exato clicado
                          // sobrescrevendo o find() genérico quando há DDIs iguais.
                          setDialPopoverOpen(false);
                        }}
                        className="flex items-center gap-3"
                      >
                        <span className="text-lg leading-none">{c.flag}</span>
                        <span className="flex-1 truncate">{c.country}</span>
                        <span className="font-mono text-sm text-muted-foreground">{c.code}</span>
                        {isSelected && <Check className="h-4 w-4 text-primary" />}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Campos exclusivos do fluxo Brasil */}
      {isBR && (
        <>
          {/* Tipo de pessoa */}
          <div className="space-y-2">
            <Label className="text-base font-medium">Tipo de Cadastro</Label>
            <RadioGroup
              value={personType}
              onValueChange={(v) => {
                setPersonType(v as 'pf' | 'pj');
                setTaxId('');
                setTaxIdError(null);
              }}
              className="grid grid-cols-2 gap-3"
            >
              <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${personType === 'pf' ? 'border-primary bg-primary/5' : 'border-border/50'}`}>
                <RadioGroupItem value="pf" />
                <span className="font-medium">Pessoa Física</span>
              </label>
              <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${personType === 'pj' ? 'border-primary bg-primary/5' : 'border-border/50'}`}>
                <RadioGroupItem value="pj" />
                <span className="font-medium">Pessoa Jurídica</span>
              </label>
            </RadioGroup>
          </div>

          {/* CPF / CNPJ */}
          <div className="space-y-2">
            <Label htmlFor="taxId" className="text-base font-medium">
              {personType === 'pf' ? 'CPF' : 'CNPJ'}
            </Label>
            <Input
              id="taxId"
              inputMode="numeric"
              placeholder={personType === 'pf' ? '000.000.000-00' : '00.000.000/0000-00'}
              value={taxId}
              onChange={(e) => handleTaxIdChange(e.target.value)}
              disabled={isRateLimited}
              className="h-12 text-base rounded-xl border-border/50 focus:border-primary transition-colors font-mono"
            />
            {taxIdError && (
              <p className="text-sm text-destructive flex items-center gap-2">
                <XCircle className="h-4 w-4" /> {taxIdError}
              </p>
            )}
          </div>

          {/* CEP + UF + Cidade */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_2fr] gap-3">
            <div className="space-y-2">
              <Label htmlFor="cep" className="text-base font-medium">CEP</Label>
              <div className="relative">
                <Input
                  id="cep"
                  inputMode="numeric"
                  placeholder="00000-000"
                  value={cep}
                  onChange={(e) => { setCep(maskCEP(e.target.value)); setCepError(null); }}
                  onBlur={handleCepBlur}
                  disabled={isRateLimited}
                  className="h-12 text-base rounded-xl font-mono pr-10"
                />
                {isLoadingCep ? (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                )}
              </div>
              {cepError && <p className="text-xs text-destructive">{cepError}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="uf" className="text-base font-medium">UF</Label>
              <Input
                id="uf"
                maxLength={2}
                value={uf}
                onChange={(e) => setUf(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                disabled={isRateLimited || isLoadingCep}
                className="h-12 w-16 text-base rounded-xl text-center font-mono uppercase"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cidade" className="text-base font-medium">Cidade</Label>
              <Input
                id="cidade"
                value={cidade}
                onChange={(e) => setCidade(e.target.value)}
                disabled={isRateLimited || isLoadingCep}
                className="h-12 text-base rounded-xl"
              />
            </div>
          </div>
        </>
      )}

      {/* Ramo de atividade (obrigatório em todos os países) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="industry" className="text-base font-medium">Ramo de atividade</Label>
          <span className={`text-xs ${industry.length >= 50 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
            {industry.length}/50
          </span>
        </div>
        <Input
          id="industry"
          placeholder="Ex: Advocacia, E-commerce, Clínica médica"
          value={industry}
          onChange={(e) => setIndustry(sanitizeInput(e.target.value).slice(0, 50))}
          maxLength={50}
          disabled={isRateLimited}
          className="h-12 text-base rounded-xl border-border/50 focus:border-primary transition-colors"
        />
      </div>

      {/* Principal dor (obrigatório em todos os países) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="mainPain" className="text-base font-medium">Qual sua principal dor?</Label>
          <span className={`text-xs ${mainPain.length >= 200 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
            {mainPain.length}/200
          </span>
        </div>
        <Textarea
          id="mainPain"
          placeholder="Ex: Perco leads porque não consigo responder rápido no WhatsApp"
          value={mainPain}
          onChange={(e) => setMainPain(sanitizeInput(e.target.value).slice(0, 200))}
          maxLength={200}
          disabled={isRateLimited}
          rows={3}
          className="text-base rounded-xl border-border/50 focus:border-primary transition-colors resize-none"
        />
      </div>

      {/* Botão de continuar */}
      <div className="pt-4">
        <Button onClick={handleCreateTenant} disabled={!canProceedToStep2 || isRateLimited || isSubmitting} className="w-full h-12 text-base rounded-xl gap-2">
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Criando empresa...
            </>
          ) : (
            <>
              Criar Empresa
              <ArrowRight className="h-5 w-5" />
            </>
          )}
        </Button>
      </div>
    </div>;

  // Helpers de plano e renderStep2Content removidos: passo "Confirme seu Plano"
  // saiu do onboarding.


  /**
   * Renderiza o conteúdo do Step 2 - Sucesso (Tudo Pronto!)
   */
  const renderStep2Content = () => <div className="text-center space-y-6 animate-fade-in">
      {/* Ícone de sucesso */}
      <div className="mx-auto w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
          <CheckCircle2 className="h-10 w-10 text-green-500" />
        </div>
      </div>

      {/* Mensagem de sucesso */}
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          Parabéns! 🎉
        </h2>
        <p className="text-muted-foreground text-lg">
          Sua empresa foi criada com sucesso
        </p>
      </div>

      {/* Detalhes criados */}
      <div className="bg-muted/30 rounded-2xl p-6 text-left border border-border/50">
        <h4 className="font-medium mb-4 text-lg">Sua empresa está pronta:</h4>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-primary" />
            <span className="text-foreground">{companyName}</span>
          </div>
          <div className="flex items-center gap-3">
            <Zap className="h-5 w-5 text-primary" />
            <span className="font-mono text-sm text-muted-foreground">
              app.uaizychat.com.br
            </span>
          </div>
        </div>
      </div>

      {/* Botão de acesso */}
      <Button onClick={handleAccessPanel} size="lg" className="w-full h-14 text-lg rounded-xl gap-3">
        <Rocket className="h-6 w-6" />
        Acessar meu Painel
      </Button>
    </div>;

  // ============================================================================
  // RENDERIZAÇÃO PRINCIPAL
  // ============================================================================

  return <div className="min-h-screen flex flex-col lg:flex-row">
      {/* ================================================================== */}
      {/* SIDEBAR ESQUERDA - STEPPER E BRANDING */}
      {/* ================================================================== */}
      <div className="hidden lg:flex lg:w-[40%] flex-col justify-between p-8 xl:p-12 relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, hsl(210, 80%, 15%) 0%, hsl(210, 70%, 20%) 50%, hsl(200, 60%, 25%) 100%)'
    }}>
        {/* Elementos decorativos de fundo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Círculos decorativos */}
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute top-1/2 -left-32 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 right-10 w-48 h-48 bg-white/5 rounded-full blur-2xl" />
          
          {/* Grid pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `
                linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
              `,
          backgroundSize: '50px 50px'
        }} />
        </div>

        {/* Stepper vertical */}
        <div className="relative z-10">
          {renderVerticalStepper()}
        </div>

        {/* Copyright */}
        <div className="relative z-10">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} {branding?.app_name || 'UaizyChat'}. Todos os direitos reservados.
          </p>
        </div>
      </div>

      {/* ================================================================== */}
      {/* ÁREA DE CONTEÚDO - FORMULÁRIOS */}
      {/* ================================================================== */}
      <div className="flex-1 flex flex-col min-h-screen bg-background relative">
        {/* Barra superior com identificação do usuário e botão Sair.
            Permite ao usuário abandonar o onboarding e voltar ao login
            mesmo se a criação do tenant estiver falhando. */}
        <div className="flex items-center justify-end gap-3 px-4 lg:px-8 pt-4 pb-2">
          {user?.email && (
            <span className="text-xs text-muted-foreground truncate max-w-[200px]" title={user.email}>
              {user.email}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
            Sair
          </Button>
        </div>

        {/* Header mobile com logo */}
        <div className="lg:hidden p-6 border-b border-border">
          <div className="flex items-center gap-3">
            {branding?.logo_url ? <img src={branding.logo_url} alt={branding.app_name || 'UaizyChat'} className="h-8 w-auto" /> : <>
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-primary" />
                </div>
                <span className="text-xl font-bold text-foreground">
                  {branding?.app_name || 'UaizyChat'}
                </span>
              </>}
          </div>
          
          {/* Stepper horizontal mobile */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {STEPS.map((step, index) => <div key={step.id} className="flex items-center">
                <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    ${currentStep > step.id ? 'bg-green-500 text-white' : currentStep === step.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
                  `}>
                  {currentStep > step.id ? <Check className="h-4 w-4" /> : step.id}
                </div>
                {index < STEPS.length - 1 && <div className={`w-8 h-0.5 mx-1 ${currentStep > step.id ? 'bg-green-500' : 'bg-muted'}`} />}
              </div>)}
          </div>
        </div>

        {/* Conteúdo principal */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            {/* Logo no topo da área de conteúdo - igual tela de login */}
            <div className="flex justify-center mb-8">
              {branding?.logo_url ? <img src={branding.logo_url} alt={branding.app_name || 'UaizyChat'} className="h-28 w-auto" /> : <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 text-primary" />
                  </div>
                  <span className="text-2xl font-bold text-foreground">
                    {branding?.app_name || 'UaizyChat'}
                  </span>
                </div>}
            </div>

            {/* Cabeçalho do step */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-foreground">
                {STEPS[currentStep - 1].title}
              </h1>
              <p className="text-muted-foreground mt-2">
                {currentStep === 1 && 'Preencha os dados básicos para configurar sua empresa.'}
                {currentStep === 2 && 'Sua empresa está pronta para uso!'}
              </p>
            </div>

            {/* Conteúdo do step atual */}
            {currentStep === 1 && renderStep1Content()}
            {currentStep === 2 && renderStep2Content()}
          </div>
        </div>
      </div>
    </div>;
}